// The context sheet of a telephone (ui-map-interface.md §12.0 "The context
// sheet"; mockup round 083 card 13, round 087 card 01, round 088 card 08).
//
// On a telephone the card of a thing stands as a sheet over the tab bar,
// full width less two `ctx_inset`, and the tab bar stays in view under it.
// The sheet has three states:
//
// - **closed**: the peek of round 082 card 14. One 44 px line (the mark,
//   the name in the owner ink, the class or the worst condition, the hull
//   n/m, the running order and ✕) and one 44 px row: the card's actions
//   (for an own fleet the order row of `ui/cards/orders.ts`) with ⋯ at
//   the right edge.
// - **raised** by a drag up or a tap on the grab handle or the line: the
//   full card, at most `ctx_object_vh` of the height for a map object and
//   `ctx_catalogue_vh` for a catalogue card, with the action row pinned to
//   the sheet foot.
// - **plot** (a v2 slot, under the flag `routes` only): the plot line in
//   place of the peek line, PLOT · n LEGS, and the row LOOP · UNDO · DONE;
//   raised, the list of the legs. PLOT of ⋯ enters it (R2589).
//
// The sheet has no ▴ and no ▾ (R2008): the grab handle at its top edge,
// the line and the card head move it. The sheet follows the finger, the
// state commits when the drag passes `ctx_swipe_px` (`ctxDragState`), and
// the release snaps to it; `ui/ctxdrag.ts` runs the drag.
//
// A menu that opens while a selection stands takes the sheet (R1748):
// under TWO, and under THREE at the full stop, the peek yields to a
// selection chip in the head of the menu; under THREE at the rest stop the
// fold of round 085 card 08 stands there instead (`selPlace`). Beside the
// landscape rail a card at the right edge has room beside the menu, so it
// lowers to its peek in place of a leave (round 090 card 05 C, R1848).
//
// Everything here is pure: it turns a card spec and a mode into markup,
// and the overlay owns the state.

import {
  LAYOUT_CTX_CATALOGUE_VH, LAYOUT_CTX_DRAG_GIVE, LAYOUT_CTX_OBJECT_VH, LAYOUT_CTX_SWIPE_PX,
  LAYOUT_CTX_TAP_PX,
} from '../gen/presentation';
import { card as S } from '../gen/strings';
import { arrowGlyph, crossGlyph } from './arrowpress';
import { esc } from './esc';
import { fillPct } from './format';
import {
  armHtml, buttonHtml, chipHtml, editPressHtml, hasMore, inkAttr, morePressHtml, type CardEnv, type CardSpec,
} from './cards/card';
import { COND_GLYPH, worstCondition } from './cards/conds';
import type { SlotKind } from './ctxslot';

/** The three states of the context sheet. */
export type CtxState = 'closed' | 'raised' | 'plot';

/** The mode the overlay holds: plot mode or the card, raised or not. */
export interface CtxMode {
  plot: boolean;
  raised: boolean;
}

/** The state a mode shows. A raised plot is still the plot state. */
export function ctxState(m: CtxMode): CtxState {
  return m.plot ? 'plot' : m.raised ? 'raised' : 'closed';
}

/** What moves the sheet. */
export type CtxEvent = 'select' | 'raise' | 'lower' | 'plot' | 'endplot';

/** The closed sheet: the state of every new selection. */
export const CTX_CLOSED: CtxMode = { plot: false, raised: false };

/**
 * One step of the state machine. A new selection opens closed; a drag up
 * or a tap raises, a drag down or a tap on the grab handle lowers (R2008);
 * `plot` enters plot mode only while
 * the flag `routes` holds (R1795), and a mode in plot falls back to the
 * closed card when the flag is off.
 */
export function ctxStep(m: CtxMode, ev: CtxEvent, routes: boolean): CtxMode {
  const base = m.plot && !routes ? CTX_CLOSED : m;
  switch (ev) {
    case 'select': case 'endplot': return CTX_CLOSED;
    case 'raise': return { ...base, raised: true };
    case 'lower': return { ...base, raised: false };
    case 'plot': return routes ? { plot: true, raised: false } : base;
  }
}

/** The cap of the raised sheet, in chrome px of a screen `vh` high. */
export function ctxCapPx(vh: number, kind: SlotKind): number {
  return Math.round(vh * (kind === 'catalogue' ? LAYOUT_CTX_CATALOGUE_VH : LAYOUT_CTX_OBJECT_VH) / 100);
}

/**
 * The state a drag holds now (R2008): a closed sheet that moved up by
 * `ctx_swipe_px` or more is raised, a raised sheet that moved down by
 * `ctx_swipe_px` or more is closed, and a drag back under the threshold
 * gives the start state back. `dy` is in chrome px, negative upward. The
 * sheet commits the state during the move, once per crossing.
 */
export function ctxDragState(startRaised: boolean, dy: number): boolean {
  return startRaised ? dy < LAYOUT_CTX_SWIPE_PX : dy <= -LAYOUT_CTX_SWIPE_PX;
}

/** A press that moved less than `ctx_tap_px` is a tap, not a drag. */
export function ctxTap(dy: number): boolean {
  return Math.abs(dy) < LAYOUT_CTX_TAP_PX;
}

/**
 * The height of the sheet under a drag (R2008): the start height `h0`
 * less `dy` (a drag up grows it), between the closed height `lo` and the
 * raised cap `hi`. Past either end the sheet moves `ctx_drag_give` of the
 * finger, so it resists and never stops dead.
 */
export function ctxDragHeight(h0: number, dy: number, lo: number, hi: number): number {
  const h = h0 - dy;
  if (h > hi) return hi + (h - hi) * LAYOUT_CTX_DRAG_GIVE;
  if (h < lo) return Math.max(0, lo - (lo - h) * LAYOUT_CTX_DRAG_GIVE);
  return h;
}

/** What a tap on the sheet does (R2008): a tap on the grab handle moves
 *  the sheet to the other state, a tap on the line of a closed sheet
 *  raises it, and every other tap moves nothing (null). */
export function ctxTapGo(raised: boolean, onGrab: boolean): boolean | null {
  if (onGrab) return !raised;
  return raised ? null : true;
}

/**
 * What ⋯ or EDIT of a fleet's card does (R1809, R2008, R2302). `open` is
 * the fleet whose grid of this press stands now, 0 for none. On a closed
 * telephone sheet the press raises the sheet and opens the grid in one
 * step, because the closed sheet draws no grid. Else the press opens the
 * grid of the selected fleet, and a second press closes it. `open` of the
 * answer is the fleet whose grid stands after the press.
 */
export function gridPress(o: { fleet: number; open: number; closedPhone: boolean }): { open: number; raise: boolean } {
  if (o.closedPhone && o.fleet !== 0) return { open: o.fleet, raise: true };
  return { open: o.fleet !== 0 && o.open !== o.fleet ? o.fleet : 0, raise: false };
}

/** Where the selection stands while the sheet setting and a menu allow. */
export type SelPlace = 'none' | 'sheet' | 'chip' | 'fold';

/**
 * The yield (087 card 01, 087 open point 2). A desktop and a telephone
 * with no open menu show the card in the slot or the sheet. A menu that
 * stands open on a telephone takes the sheet: under TWO, and under THREE
 * at the full stop, the selection yields to the chip in the head of the
 * menu; under THREE at the rest stop (or a free height of a drag) the
 * fold of 085 card 08 stands there, because the map shows over the sheet.
 * Beside the landscape rail a card at the right edge (`beside`) stands
 * beside the menu, and no chip is needed (round 090 card 05 C).
 */
export function selPlace(o: {
  selection: boolean; phone: boolean; menuOpen: boolean; three: boolean; expanded: boolean; beside?: boolean;
}): SelPlace {
  if (!o.selection) return 'none';
  if (!o.phone || !o.menuOpen || o.beside) return 'sheet';
  return o.three && !o.expanded ? 'fold' : 'chip';
}

/**
 * The card beside a menu that opens (round 090 card 05 C). Beside the
 * landscape rail a menu that opens lowers a raised card to its peek in
 * place of a leave: the menu takes the column, and the peek stands at
 * the right edge. The grab handle raises it again beside the menu, and ✕
 * closes it. The raised list of a plot line stays (R2589): the legs are
 * what the commander plots with, and a menu must not take them away.
 * `opened` is true on the one render where the menu opens.
 */
export function menuYield(m: CtxMode, opened: boolean, beside: boolean): CtxMode {
  return opened && beside && m.raised && !m.plot ? { ...m, raised: false } : m;
}

// --- the markup -------------------------------------------------------------

/**
 * The grab handle of the sheet (R2008): a short bar centred on the top
 * edge, in the look of the bar of the dock grip, with a 44 px target. A
 * drag on it moves the sheet, and a press moves the sheet to the other
 * state, so a keyboard keeps the way that ▴ and ▾ gave. It carries no
 * `data-act`: the drag of `ui/ctxdrag.ts` answers it, and its name
 * says what a press does.
 */
export function grabHtml(raised: boolean): string {
  const name = esc(raised ? S.lower_title : S.raise_title);
  return `<button type="button" class="cs-grab" title="${name}" aria-label="${name}"`
    + ` aria-expanded="${raised}"><i aria-hidden="true"></i></button>`;
}

/** ✕ of the sheet: a 44 px quiet press with the glyph in the 12 px box,
 *  the look of EDIT beside it. It closes the card and clears the
 *  selection. */
export function sheetCloseHtml(act = 'deselect', title: string = S.close_title): string {
  return `<button class="ck-b qt cs-x" data-act="${act}" title="${esc(title)}"`
    + ` aria-label="${esc(title)}">${crossGlyph()}</button>`;
}

/** The second line of the peek line: the class, or the worst condition
 *  while a bad one holds (v2 slot 1, R1808). */
function kindLine(spec: CardSpec): string {
  const worst = worstCondition(spec.conds);
  if (worst) {
    return `<i class="cs-k cs-cond bad"${worst.why ? ` title="${esc(worst.why)}"` : ''}>`
      + `${esc(worst.icon ?? COND_GLYPH.bad)} ${esc(worst.label)}`
      + (worst.value ? ` <em>${esc(worst.value)}</em>` : '') + '</i>';
  }
  return spec.kind ? `<i class="cs-k">${esc(spec.kind)}</i>` : '';
}

/** The hull of the line: the first vital of the card, as a figure and a bar. */
function hullPart(spec: CardSpec): string {
  const v = spec.vitals?.[0];
  if (!v) return '';
  const bar = ['ck-bar', `i-${v.ink ?? 'hull'}`];
  if (v.unknown) bar.push('unk');
  if (v.low && !v.unknown) bar.push('low');
  return `<span class="cs-hull"><span>${v.unknown ? esc(S.unknown) : v.fig}</span>`
    + `<i class="${bar.join(' ')}"><b style="width:${fillPct(v.unknown ? 0 : v.frac)}"></b></i></span>`;
}

/** The peek line: the mark, the name, the class, the hull, the order, ✕. */
function lineHtml(spec: CardSpec): string {
  return '<div class="cs-line">'
    + (spec.mark ? `<span class="ck-mk">${spec.mark}</span>` : '')
    + `<span class="cs-nw"><b${inkAttr(spec.ink)}>${esc(spec.title)}</b>${kindLine(spec)}</span>`
    + hullPart(spec)
    + (spec.status ? chipHtml(spec.status.text, spec.status.tone) : '')
    + (spec.close ? sheetCloseHtml() : '')
    + '</div>';
}

/**
 * The closed sheet: the peek line, the armed order while one is armed
 * (R2164), the card's `closedRow` (the signature order of an own hull,
 * R2415), and the action row, with ⋯ at its right edge when the card
 * holds more orders (R2008). A press on that ⋯ raises
 * the sheet and opens the grid in one step. A card with no action and no
 * ⋯ keeps the line alone.
 */
export function closedHtml(spec: CardSpec, env: CardEnv): string {
  const acts = spec.actions ?? [];
  const more = (spec.edit ? editPressHtml({ ...spec.edit, open: false }) : '')
    + (spec.more && hasMore(spec.more) ? morePressHtml({ ...spec.more, open: false }) : '');
  // The armed order stands under the line (R2164): the closed sheet is the
  // place a telephone reads it. EDIT stands on the action row (R2196).
  const row = acts.length === 0 && more === '' ? ''
    : `<div class="cs-row">${acts.map((a) => buttonHtml(a, env)).join('')}${more}</div>`;
  return `<div class="ck cs">${lineHtml(spec)}${armHtml(spec.armed, env)}${spec.closedRow ?? ''}${row}</div>`;
}

/** The raised card: the full card with the sheet's ✕ in its head. */
export function raisedSpec(spec: CardSpec): CardSpec {
  return {
    ...spec, close: false,
    headExtra: (spec.headExtra ?? '') + (spec.close ? sheetCloseHtml(spec.closeAct) : ''),
  };
}

/** The selection chip in the head of a menu sheet: the mark, the name and
 *  ▾. A press closes the menu, and the peek comes back. */
export function selChipHtml(spec: CardSpec): string {
  return `<button class="selchip" data-act="selchip" title="${esc(S.selchip_title)}"`
    + ` aria-label="${esc(S.selchip_title)}">`
    + (spec.mark ? `<span class="ck-mk">${spec.mark}</span>` : '')
    + `<b${inkAttr(spec.ink)}>${esc(spec.title)}</b><i>${arrowGlyph('down')}</i></button>`;
}

/** The fold of 085 card 08 (THREE at the rest stop): the selection as one
 *  line at the head of the menu sheet, with the grab handle and ✕. A tap
 *  on the line or the handle closes the menu and raises the card. */
export function foldHtml(spec: CardSpec): string {
  return `<div class="ck cs cs-fold">${grabHtml(false)}${lineHtml(spec)}</div>`;
}
